import { Volume } from '@/api/type-server'
import { useUserStore } from '@/stores/user'
import { getEffectiveQuota } from '@/utils/storage'

import { BarSegment, ColorScheme, SegmentedProgressBar } from './SegmentedProgressBar'

const DATADRIVE_GRADIENT = [
  'bg-btn-secondary',
  'bg-btn-secondary/70',
  'bg-btn-secondary/45',
  'bg-btn-secondary/25',
]
const DATADRIVE_GRADIENT_EXPIRED = [
  'bg-bar-tertiary',
  'bg-bar-tertiary/70',
  'bg-bar-tertiary/45',
  'bg-bar-tertiary/25',
]

export function getDatadriveGradientClass(index: number, expired = false): string {
  const palette = expired ? DATADRIVE_GRADIENT_EXPIRED : DATADRIVE_GRADIENT
  return palette[Math.min(index, palette.length - 1)]
}

interface VolumeUsageSegmentedBarProps {
  snapshotVolumes?: Volume[]
  cloudVolumes?: Volume[]
  totalQuota?: number // Optional: external quota (for team pages)
  isExpired?: boolean // Optional: external expired state (for team pages)
  imageOnSpaceBilling?: boolean
  snapshotOnSpaceBilling?: boolean
}

export function VolumeUsageSegmentedBar({
  snapshotVolumes = [],
  cloudVolumes = [],
  totalQuota,
  isExpired,
  imageOnSpaceBilling = true,
  snapshotOnSpaceBilling = true,
}: VolumeUsageSegmentedBarProps) {
  const userStore = useUserStore()

  // Determine quota: prioritize external quota, fallback to userStore
  let partVolumeQuotaInGB: number
  let colorScheme: ColorScheme

  if (totalQuota !== undefined) {
    // External quota provided (e.g., team pages)
    partVolumeQuotaInGB = totalQuota
    // Use expired color scheme if isExpired is explicitly passed
    colorScheme = isExpired ? 'expired' : 'normal'
  } else if (userStore.isOrderExpired) {
    // User order expired: use last quota
    partVolumeQuotaInGB = userStore.partVolume?.last_quota_gb || 0
    colorScheme = 'expired'
  } else if (!userStore.order) {
    // No package
    partVolumeQuotaInGB = userStore.partVolume?.quota_gb || 0
    colorScheme = 'noPackage'
  } else {
    // Normal user quota
    partVolumeQuotaInGB = userStore.partVolume?.quota_gb || 0
    colorScheme = 'normal'
  }

  if (partVolumeQuotaInGB <= 0) {
    return <SegmentedProgressBar value={0} secondValue={0} colorScheme={colorScheme} />
  }

  // Calculate quota sums
  // Use last_quota_gb when: personal order expired OR team isExpired is true
  const useLastQuota = (totalQuota === undefined && userStore.isOrderExpired) || isExpired
  const snapshotQuotaSum = snapshotVolumes.reduce(
    (sum, vol) => sum + getEffectiveQuota(vol, useLastQuota ?? false),
    0,
  )
  const cloudQuotaSum = cloudVolumes.reduce(
    (sum, vol) => sum + getEffectiveQuota(vol, useLastQuota ?? false),
    0,
  )

  if (imageOnSpaceBilling && snapshotOnSpaceBilling) {
    const snapshotQuota = snapshotQuotaSum / partVolumeQuotaInGB
    const snapshotAndCloudQuota = (snapshotQuotaSum + cloudQuotaSum) / partVolumeQuotaInGB

    return (
      <SegmentedProgressBar
        value={snapshotQuota}
        secondValue={snapshotAndCloudQuota}
        colorScheme={colorScheme}
      />
    )
  }

  const expired = colorScheme === 'expired'
  const segments: BarSegment[] = []

  if (snapshotOnSpaceBilling) {
    segments.push({
      ratio: snapshotQuotaSum / partVolumeQuotaInGB,
      className: expired ? 'bg-bar-tertiary' : 'bg-btn-tertiary',
    })
  }

  if (!imageOnSpaceBilling && !snapshotOnSpaceBilling) {
    cloudVolumes.forEach((volume, index) => {
      const quota = getEffectiveQuota(volume, useLastQuota ?? false)
      if (quota > 0) {
        segments.push({
          ratio: quota / partVolumeQuotaInGB,
          className: getDatadriveGradientClass(index, expired),
        })
      }
    })
  } else {
    segments.push({
      ratio: cloudQuotaSum / partVolumeQuotaInGB,
      className: expired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary',
    })
  }

  const remainderClassName = imageOnSpaceBilling
    ? expired
      ? 'bg-tertiary'
      : 'bg-active'
    : 'bg-neutral-200'

  return (
    <SegmentedProgressBar
      value={0}
      segments={segments}
      colorScheme={colorScheme}
      remainderClassName={remainderClassName}
    />
  )
}
